import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaMoneyBillWave, FaFileAlt, FaSyringe, FaThermometerHalf, FaBell } from "react-icons/fa";
import moment from "moment";
import { MdAccessTime, MdDateRange } from "react-icons/md";
import Select from "react-select";
import Swal from 'sweetalert2';

export default function PetVaccineComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petVaccines, setPetVaccines] = useState([]);
    const [vaccines, setVaccines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingVaccine, setEditingVaccine] = useState(null);
    const [selectedVaccine, setSelectedVaccine] = useState("");
    const [selectedVaccines, setSelectedVaccines] = useState([]);
    const [selectedVaccineRows, setSelectedVaccineRows] = useState([]);
    const [vaccinePrice, setVaccinePrice] = useState("");
    const [recomendation, setRecomendation] = useState("");
    const [temp, setTemp] = useState("");
    const [vaccineDate, setVaccineDate] = useState("");
    const [reminderDate, setReminderDate] = useState("");
    const [generalNote, setGeneralNote] = useState("");
    const [existingNoteId, setExistingNoteId] = useState(null);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [openNestedAccordion, setOpenNestedAccordion] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [isRecommended, setIsRecommended] = useState(false);
    const [completeModal, setCompleteModal] = useState(false);
    const [selectedCompleteVaccine, setSelectedCompleteVaccine] = useState(null);
    const [completePrice, setCompletePrice] = useState("");
    const [completeReminderDate, setCompleteReminderDate] = useState("");
    const [completeStatus, setCompleteStatus] = useState("pending");
    const [hasVaccineForAppointment, setHasVaccineForAppointment] = useState(false);

    // Get user role from localStorage
    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Delete permission only for Admin
    const canDelete = () => {
        return userRole === 'Admin';
    };

    // Show toast messages
    const showToast = (icon, title, message = '') => {
        const Toast = Swal.mixin({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
        Toast.fire({ icon, title, text: message });
    };

    // Helper function to format price
    const formatPrice = (price) => {
        if (price === null || price === undefined || price === "") return '';
        const num = parseFloat(price);
        if (isNaN(num)) return '';
        return num % 1 === 0 ? num.toString() : num;
    };

    useEffect(() => {
        fetchPetVaccines();
    }, [appointmentId, petId]);

    const fetchPetVaccines = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-vaccine/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                const vaccinesForPet = response.data.petVaccine || [];
                setPetVaccines(vaccinesForPet);
                setVaccines(response.data.vaccine || []);
                const hasVaccine = vaccinesForPet.some(vaccine => String(vaccine?.appointment_id) === String(appointmentId));
                setHasVaccineForAppointment(hasVaccine);
                setOpenAccordion(String(appointmentId));
            }
        } catch (error) {
            console.error("Error fetching pet vaccines:", error);
            showToast('error', t('failed_to_load_vaccines'));
            setPetVaccines([]);
            setVaccines([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return null;
            return {
                date: date.toLocaleDateString(),
                time: date.toLocaleTimeString(),
                full: date.toLocaleString()
            };
        } catch (error) {
            return null;
        }
    };

    const findVaccineById = (id) => vaccines.find(v => v.id === id);

    const handleVaccineRowChange = (vaccineId, field, value) => {
        setSelectedVaccineRows(prevRows => prevRows.map(row => row.vaccine_id === vaccineId ? {
            ...row,
            [field]: value,
        } : row));
    };

    const removeSelectedVaccineRow = (vaccineId) => {
        setSelectedVaccines(prev => prev.filter(id => id !== vaccineId));
        setSelectedVaccineRows(prev => prev.filter(row => row.vaccine_id !== vaccineId));
    };

    const handleVaccineSelect = (selected) => {
        if (editingVaccine) {
            const vaccineId = selected?.value;
            setSelectedVaccine(vaccineId || "");
            const selectedVaccineData = findVaccineById(vaccineId);
            if (selectedVaccineData) {
                setVaccinePrice(selectedVaccineData.sale_price || "");
            } else {
                setVaccinePrice("");
            }
            setSelectedVaccineRows([{
                vaccine_id: vaccineId,
                vaccine_name: selectedVaccineData?.product?.name || selectedVaccineData?.product_name || selectedVaccineData?.name || '',
                price: selectedVaccineData?.sale_price || "",
                temp: temp || '',
                vaccine_date: vaccineDate || '',
                reminder_date: reminderDate || ''
            }]);
        } else {
            const vaccineIds = Array.isArray(selected) ? selected.map(item => item.value) : [];
            setSelectedVaccines(vaccineIds);
            setSelectedVaccineRows(prevRows => {
                return vaccineIds.map(id => {
                    const existing = prevRows.find(row => row.vaccine_id === id);
                    const vaccineData = findVaccineById(id);
                    return {
                        vaccine_id: id,
                        vaccine_name: vaccineData?.product?.name || vaccineData?.product_name || vaccineData?.name || '',
                        price: existing ? existing.price : (vaccineData?.sale_price || ""),
                        temp: existing ? existing.temp : '',
                        vaccine_date: existing ? existing.vaccine_date : '',
                        reminder_date: existing ? existing.reminder_date : '',
                    };
                });
            });
        }
    };

    const handleAddVaccine = () => {
        setEditingVaccine(null);
        setSelectedVaccine("");
        setSelectedVaccines([]);
        setSelectedVaccineRows([]);
        setVaccinePrice("");
        setRecomendation("");
        setTemp("");
        setVaccineDate("");
        setReminderDate("");
        setGeneralNote("");
        setExistingNoteId(null);
        setIsRecommended(false);
        setSelectedCompleteVaccine(null);
        setShowModal(true);
    };

    const handleEditVaccine = (vaccine) => {
        setEditingVaccine(vaccine);
        setSelectedVaccine(vaccine.vaccine_id || "");
        setVaccinePrice(vaccine.price !== null && vaccine.price !== undefined ? vaccine.price.toString() : "");
        setRecomendation(vaccine.recomendation || "");
        setTemp(vaccine.temp || "");
        setVaccineDate(vaccine.vaccine_date || "");
        setReminderDate(vaccine.reminder_date || "");
        setIsRecommended(!!(vaccine.recomendation && vaccine.recomendation.trim()));

        if (vaccine.note && Array.isArray(vaccine.note) && vaccine.note.length > 0) {
            setGeneralNote(vaccine.note[0].note || "");
            setExistingNoteId(vaccine.note[0].id);
        } else {
            setGeneralNote("");
            setExistingNoteId(null);
        }

        setSelectedVaccines([vaccine.vaccine_id].filter(Boolean));
        setSelectedVaccineRows([{
            vaccine_id: vaccine.vaccine_id,
            vaccine_name: vaccine.vaccine?.product?.name || vaccine.vaccine?.product_name || vaccine.vaccine?.name || '',
            price: vaccine.price !== null && vaccine.price !== undefined ? vaccine.price.toString() : "",
            temp: vaccine.temp || '',
            vaccine_date: vaccine.vaccine_date || '',
            reminder_date: vaccine.reminder_date || '',
        }]);
        setShowModal(true);
    };

    const handleDeleteVaccine = async (vaccineId) => {
        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-vaccine-delete/${vaccineId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                fetchPetVaccines();
                showToast('success', t('Vaccine deleted successfully!'));
            }
        } catch (error) {
            console.error("Error deleting vaccine:", error);
            showToast('error', t('failed_to_delete_vaccine'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (vaccineId, vaccineName) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t(`You won't be able to revert this!`),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeleteVaccine(vaccineId);
            }
        });
    };

    const handleOpenComplete = (vaccine) => {
        setSelectedCompleteVaccine(vaccine);
        setCompletePrice(vaccine.price !== null && vaccine.price !== undefined && vaccine.price !== 0 ? vaccine.price.toString() : "");
        setCompleteReminderDate(vaccine.reminder_date || "");
        setCompleteStatus(vaccine.reminder_status || "pending");
        setCompleteModal(true);
    };

    const resetCompleteModal = () => {
        setCompleteModal(false);
        setSelectedCompleteVaccine(null);
        setCompletePrice("");
        setCompleteReminderDate("");
        setCompleteStatus("pending");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");

            if (!selectedCompleteVaccine && !editingVaccine && !isRecommended && selectedVaccineRows.length === 0) {
                showToast('warning', t('please select vaccine'));
                setSubmitting(false);
                return;
            }

            let url = "";
            let payload = {};

            // Complete Vaccine Flow (Update)
            if (selectedCompleteVaccine) {
                url = `${process.env.REACT_APP_API_URL}/pet-vaccine-update/${selectedCompleteVaccine.id}`;
                payload = {
                    appointment_id: appointmentId,
                    is_repeat: true,
                    vaccine_id: selectedCompleteVaccine.vaccine_id,
                    price: completePrice === "" ? 0 : parseFloat(completePrice) || 0,
                    reminder_date: completeReminderDate,
                    reminder_status: completeStatus,
                };

                const response = await axios.post(url, payload, {
                    headers: { Authorization: `Bearer ${AuthToken}` }
                });

                if (response.data) {
                    resetCompleteModal();
                    fetchPetVaccines();
                    showToast('success', t('Vaccine completed successfully!'));
                }
            }
            // Add Vaccine Flow (Store) - Multiple vaccines
            else if (!isRecommended && !editingVaccine) {
                // Prepare vaccine_rows array as per backend expectation
                const vaccineRows = selectedVaccineRows.map(row => ({
                    vaccine_id: parseInt(row.vaccine_id),
                    price: row.price === "" ? 0 : parseFloat(row.price) || 0,
                    temp: row.temp || null,
                    vaccine_date: row.vaccine_date || null,
                    reminder_date: row.reminder_date || null,
                }));

                payload = {
                    appointment_id: parseInt(appointmentId),
                    vaccine_rows: vaccineRows,
                };

                // Add note if exists
                if (generalNote && generalNote.trim()) {
                    payload.note = generalNote.trim();
                }

                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/pet-vaccine`,
                    payload,
                    { headers: { Authorization: `Bearer ${AuthToken}` } }
                );

                if (response.data) {
                    setShowModal(false);
                    fetchPetVaccines();
                    showToast('success', t('Vaccines added successfully!'));

                    // Reset form
                    setSelectedVaccine("");
                    setSelectedVaccines([]);
                    setSelectedVaccineRows([]);
                    setVaccinePrice("");
                    setTemp("");
                    setVaccineDate("");
                    setReminderDate("");
                    setGeneralNote("");
                    setExistingNoteId(null);
                }
            }
            // Edit Vaccine Flow (Update) - Single vaccine
            else if (!isRecommended && editingVaccine) {
                url = `${process.env.REACT_APP_API_URL}/pet-vaccine-update/${editingVaccine.id}`;
                payload = {
                    appointment_id: appointmentId,
                    vaccine_id: parseInt(selectedVaccine),
                    price: vaccinePrice === "" ? 0 : parseFloat(vaccinePrice) || 0,
                    temp: temp || null,
                    vaccine_date: vaccineDate || null,
                    reminder_date: reminderDate || null,
                    is_repeat: false,
                };

                if (generalNote && generalNote.trim()) {
                    payload.note = generalNote.trim();
                    if (existingNoteId) {
                        payload.note_id = existingNoteId;
                    }
                } else if (existingNoteId) {
                    payload.note = "";
                    payload.note_id = existingNoteId;
                }

                const response = await axios.post(url, payload, {
                    headers: { Authorization: `Bearer ${AuthToken}` }
                });

                if (response.data) {
                    setShowModal(false);
                    fetchPetVaccines();
                    showToast('success', t('Vaccine updated successfully!'));
                    setEditingVaccine(null);
                }
            }
            // Recommended Flow
            else if (isRecommended) {
                payload = {
                    appointment_id: parseInt(appointmentId),
                    recomendation: recomendation || null,
                    vaccine_date: vaccineDate || null,
                };

                if (generalNote && generalNote.trim()) {
                    payload.note = generalNote.trim();
                }

                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/pet-vaccine`,
                    payload,
                    { headers: { Authorization: `Bearer ${AuthToken}` } }
                );

                if (response.data) {
                    setShowModal(false);
                    fetchPetVaccines();
                    showToast('success', t('Recommendation added successfully!'));
                    setRecomendation("");
                }
            }

        } catch (error) {
            console.error("Error saving vaccine:", error);
            showToast('error', error.response?.data?.message || t('failed_to_save_vaccine'));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading vaccines')}</p>
            </div>
        );
    }

    const vaccineOptions = vaccines.map(v => ({
        value: v.id,
        label: `${v.product_name}`
    }));

    const groupVaccinesByAppointment = () => {
        const grouped = {};
        petVaccines.forEach(vaccine => {
            const vaccineAppointmentId = vaccine.appointment_id;
            if (!grouped[vaccineAppointmentId]) {
                grouped[vaccineAppointmentId] = {
                    appointment: vaccine.appointment,
                    vaccines: []
                };
            }
            grouped[vaccineAppointmentId].vaccines.push(vaccine);
        });

        const sortedEntries = Object.entries(grouped).sort((a, b) => {
            const aId = String(a[0]);
            const bId = String(b[0]);
            const currentId = String(appointmentId);

            if (aId === currentId) return -1;
            if (bId === currentId) return 1;
            return 0;
        });

        return Object.fromEntries(sortedEntries);
    };

    const getReminderStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'pending':
                return 'warning';
            case 'missed':
                return 'danger';
            case 'cancelled':
                return 'secondary';
            default:
                return 'secondary';
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted">{t('vaccines list')}</h6>
                {isAppointmentOpen && !hasVaccineForAppointment && hasPermission("Appointment Vaccine Create") && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddVaccine}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaPlus size={12} /> {t('add vaccine')}
                    </Button>
                )}
            </div>

            {!petVaccines || petVaccines.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaSyringe size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no vaccines found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key)}
                    className="appointments-accordion"
                >
                    {Object.entries(groupVaccinesByAppointment()).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, vaccines: appointmentVaccines } = appointmentData;
                        const isCurrentAppointment = String(appointmentIdKey) === String(appointmentId);

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>
                                            <div className="text-muted small">
                                                👨‍⚕️ <strong>Doctor:</strong> {appointment?.doctor?.user?.name || 'Unknown Doctor'}
                                            </div>
                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentVaccines.length} {t('vaccine(s)')}
                                            </Badge>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white p-0">
                                    <Accordion
                                        activeKey={openNestedAccordion[appointmentIdKey]}
                                        onSelect={(key) => setOpenNestedAccordion({
                                            ...openNestedAccordion,
                                            [appointmentIdKey]: key
                                        })}
                                        className="nested-vaccines-accordion pe-3 ps-3 pt-3"
                                    >
                                        {appointmentVaccines.map((vaccine) => {
                                            if (!vaccine) return null;
                                            const dateTime = formatDateTime(vaccine.vaccine_date);
                                            const noteText = vaccine.note && Array.isArray(vaccine.note) && vaccine.note.length > 0
                                                ? vaccine.note[0].note
                                                : null;

                                            return (
                                                <Accordion.Item eventKey={String(vaccine.id)} key={vaccine.id} className="mb-3 border rounded">
                                                    <Accordion.Header className="bg-light">
                                                        <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                                                <FaStethoscope className="text-primary" />
                                                                {!vaccine.recomendation && (
                                                                    <Badge bg="success" className="px-3 py-2">
                                                                        {vaccine.vaccine?.product?.name || vaccine.vaccine?.name || 'N/A'}
                                                                    </Badge>
                                                                )}
                                                                {vaccine.recomendation && (
                                                                    <Badge bg="warning" className="px-3 py-2">
                                                                        {t('Recommended')}
                                                                    </Badge>
                                                                )}
                                                                {dateTime && !vaccine.recomendation && (
                                                                    <div className="d-flex align-items-center gap-3 text-muted small">
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <MdDateRange size={14} />
                                                                            {moment(dateTime.date).format("DD MMM YYYY")}
                                                                        </span>
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <MdAccessTime size={14} />
                                                                            {moment(dateTime.time, "HH:mm:ss").format("hh:mm A")}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {vaccine.reminder_status && !vaccine.recomendation && (
                                                                    <Badge bg={getReminderStatusBadge(vaccine.reminder_status)} className="px-3 py-2">
                                                                        <FaBell className="me-1" size={10} />
                                                                        {vaccine.reminder_status}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                                {hasPermission("Appointment Vaccine edit") && (
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={() => handleEditVaccine(vaccine)}
                                                                        className="d-flex align-items-center gap-1"
                                                                        style={{ background: '#3182ce', color: '#fff' }}
                                                                    >
                                                                        <FaEdit size={12} />
                                                                        <span className="d-none d-sm-inline">{t('edit')}</span>
                                                                    </Button>
                                                                )}
                                                                {canDelete() && (
                                                                    <Button
                                                                        variant="danger"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteClick(vaccine.id, vaccine.vaccine?.product?.name)}
                                                                        className="d-flex align-items-center gap-1"
                                                                    >
                                                                        <FaTrash size={12} />
                                                                        <span className="d-none d-sm-inline">{t('delete')}</span>
                                                                    </Button>
                                                                )}
                                                                {isAppointmentOpen && String(vaccine.appointment_id) !== String(appointmentId) && vaccine.reminder_date && (!vaccine.reminder_status || vaccine.reminder_status === 'pending' || vaccine.reminder_status === 'processing') && hasPermission("Appointment Vaccine edit") && (
                                                                    <Button
                                                                        variant="success"
                                                                        size="sm"
                                                                        onClick={() => handleOpenComplete(vaccine)}
                                                                        className="d-flex align-items-center gap-1"
                                                                    >
                                                                        <FaBell size={12} />
                                                                        {t('Complete')}
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body className="bg-white">
                                                        <Row className="g-4">
                                                            <Col md={12}>
                                                                <div className="p-3 bg-light rounded">
                                                                    {!vaccine.recomendation ? (
                                                                        <>
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Vaccine Name')}:</strong> {vaccine.vaccine?.product?.name || vaccine.vaccine?.name || 'N/A'}
                                                                            </div>
                                                                            {vaccine.price !== null && vaccine.price !== undefined && vaccine.price !== 0 && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Price')}:</strong> {formatPrice(vaccine.price)}
                                                                                </div>
                                                                            )}
                                                                            {vaccine.temp && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Temperature')}:</strong> {vaccine.temp}°C
                                                                                </div>
                                                                            )}
                                                                            {vaccine.vaccine_date && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Vaccine Date')}:</strong>{' '}
                                                                                    {moment(vaccine.vaccine_date).format("DD MMM YYYY hh:mm A")}
                                                                                </div>
                                                                            )}
                                                                            {vaccine.reminder_date && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Reminder Date')}:</strong>{' '}
                                                                                    {moment(vaccine.reminder_date).format("DD MMM YYYY hh:mm A")}
                                                                                </div>
                                                                            )}
                                                                            {vaccine.reminder_status && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Reminder Status')}:</strong>{' '}
                                                                                    <Badge bg={getReminderStatusBadge(vaccine.reminder_status)}>
                                                                                        {vaccine.reminder_status}
                                                                                    </Badge>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            {vaccine.recomendation && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Recommendation')}:</strong> {vaccine.recomendation}
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    {noteText && (
                                                                        <div className="mt-3 pt-2 border-top">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <FaFileAlt className="text-success" />
                                                                                <strong className="text-muted">{t('note')}:</strong>
                                                                            </div>
                                                                            <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            );
                                        })}
                                    </Accordion>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            {/* Complete Modal */}
            <Modal show={completeModal} onHide={resetCompleteModal}>
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t('Complete Vaccine')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Status')}</Form.Label>
                        <Form.Select
                            value={completeStatus}
                            onChange={(e) => setCompleteStatus(e.target.value)}
                        >
                            <option value="completed">{t('Completed')}</option>
                            <option value="pending">{t('Pending')}</option>
                            <option value="missed">{t('Missed')}</option>
                            <option value="cancelled">{t('Cancelled')}</option>
                        </Form.Select>
                    </Form.Group>

                    <div className="mb-3">
                        <Badge bg={getReminderStatusBadge(completeStatus)} className="px-3 py-2">
                            {completeStatus === "completed" ? t('Completed') : completeStatus === "pending" ? t('Pending Completion') : completeStatus === "missed" ? t('Missed') : t('Cancelled')}
                        </Badge>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('Vaccine Name')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={selectedCompleteVaccine?.vaccine?.product?.name || ""}
                            disabled
                        />
                    </Form.Group>

                    {/* Price Field - Always visible */}
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold d-flex align-items-center gap-2">
                            <FaMoneyBillWave className="text-success" /> {t('Price')}
                        </Form.Label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            value={completePrice}
                            onClick={(e) => e.target.select()}
                            onChange={(e) => setCompletePrice(e.target.value)}
                            placeholder={t('Enter price')}
                        />
                        <small className="text-muted d-block mt-1">
                            {t('You can update the price for this vaccine')}
                        </small>
                    </Form.Group>

                    {completeStatus !== "completed" && (
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaBell /> {t('Next Reminder Date')}
                            </Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={completeReminderDate}
                                onChange={(e) => setCompleteReminderDate(e.target.value)}
                            />
                            <small className="text-muted d-block mt-1">
                                {t('Set next reminder date if needed')}
                            </small>
                        </Form.Group>
                    )}

                    {completeStatus === "completed" && (
                        <Alert variant="info" className="mt-2 p-2">
                            <FaBell className="me-1" /> {t('Vaccine marked as completed. No further reminders will be sent.')}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer className="p-2">
                    <Button variant="secondary" onClick={resetCompleteModal}>
                        {t('cancel')}
                    </Button>
                    <Button variant="success" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? <Spinner animation="border" size="sm" /> : t('Save & Complete')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>
                        {editingVaccine ? t('Edit Vaccine') : t('Add Vaccines')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="recommended-checkbox"
                                label={<span className="fw-bold">{t('Recommended')}</span>}
                                checked={isRecommended}
                                onChange={(e) => setIsRecommended(e.target.checked)}
                            />
                        </Form.Group>

                        {!isRecommended ? (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {editingVaccine ? t('Select Vaccine') : t('Select Vaccines')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Select
                                        options={vaccineOptions}
                                        value={editingVaccine ? vaccineOptions.find(opt => opt.value === selectedVaccine) || null : vaccineOptions.filter(opt => selectedVaccines.includes(opt.value))}
                                        onChange={handleVaccineSelect}
                                        placeholder={t('select vaccine')}
                                        isSearchable
                                        isMulti={!editingVaccine}
                                    />
                                    {!editingVaccine && (
                                        <small className="text-muted d-block mt-1">
                                            {t('You can select multiple vaccines to add at once.')}
                                        </small>
                                    )}
                                </Form.Group>

                                {editingVaccine ? (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold">
                                                <FaMoneyBillWave className="text-success me-1" /> {t('Price')}
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                onClick={(e) => e.target.select()}
                                                value={vaccinePrice}
                                                onChange={(e) => setVaccinePrice(e.target.value)}
                                                placeholder={t('Enter vaccine price')}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold">
                                                <FaThermometerHalf /> {t('Temperature')} (°C)
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.1"
                                                onClick={(e) => e.target.select()}
                                                value={temp}
                                                onChange={(e) => setTemp(e.target.value)}
                                                placeholder={t('Enter temperature')}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold">
                                                <FaBell /> {t('Reminder Date')}
                                            </Form.Label>
                                            <Form.Control
                                                type="datetime-local"
                                                value={reminderDate}
                                                onChange={(e) => setReminderDate(e.target.value)}
                                            />
                                        </Form.Group>
                                    </>
                                ) : (
                                    selectedVaccineRows.length > 0 && (
                                        <div className="mb-3">
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-hover align-middle mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>{t('Vaccine')}</th>
                                                            <th>{t('Price')}</th>
                                                            <th>{t('Temperature')}</th>
                                                            <th>{t('Reminder Date')}</th>
                                                            <th>{t('Action')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {selectedVaccineRows.map(row => (
                                                            <tr key={row.vaccine_id}>
                                                                <td className="fw-semibold">{row.vaccine_name || t('N/A')}</td>
                                                                <td>
                                                                    <Form.Control
                                                                        size="sm"
                                                                        type="number"
                                                                        step="0.01"
                                                                        placeholder={t('Price')}
                                                                        value={row.price}
                                                                        onClick={(e) => e.target.select()}
                                                                        onChange={(e) => handleVaccineRowChange(row.vaccine_id, 'price', e.target.value)}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <Form.Control
                                                                        size="sm"
                                                                        type="number"
                                                                        placeholder={t('Temp')}
                                                                        step="0.1"
                                                                        onClick={(e) => e.target.select()}
                                                                        value={row.temp}
                                                                        onChange={(e) => handleVaccineRowChange(row.vaccine_id, 'temp', e.target.value)}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <Form.Control
                                                                        size="sm"
                                                                        type="datetime-local"
                                                                        value={row.reminder_date}
                                                                        onChange={(e) => handleVaccineRowChange(row.vaccine_id, 'reminder_date', e.target.value)}
                                                                    />
                                                                </td>
                                                                <td className="text-center">
                                                                    <Button
                                                                        variant="danger"
                                                                        size="sm"
                                                                        onClick={() => removeSelectedVaccineRow(row.vaccine_id)}
                                                                    >
                                                                        {t('Remove')}
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="text-end mt-2">
                                                <strong>{t('Total Vaccines')}: </strong> {selectedVaccineRows.length}
                                            </div>
                                        </div>
                                    )
                                )}
                            </>
                        ) : (
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">
                                    {t('Recommendations')}
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={recomendation}
                                    onChange={(e) => setRecomendation(e.target.value)}
                                    placeholder={t('Enter recommendations here...')}
                                />
                            </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                <FaFileAlt /> {t('Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('Add any additional notes here...')}
                            />
                            {!editingVaccine && selectedVaccineRows.length > 1 && (
                                <small className="text-muted d-block mt-1">
                                    {t('Note will be applied to all selected vaccines')}
                                </small>
                            )}
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="p-2">
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            {t('cancel')}
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting}>
                            {submitting ? <Spinner animation="border" size="sm" /> : (editingVaccine ? t('update') : t('save'))}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}